<script lang="ts">
  import { fly } from 'svelte/transition';
  import { BOOKING_URL } from '$lib/links';
  import { lastCta, submitErrorType, track as send, trackCtaClick } from '$lib/utils/analytics';
  import { submitHubspotForm } from '$lib/utils/hubspot';

  type Question = { key: 'agents' | 'concern' | 'volume'; title: string; multi: boolean; choices: Array<{ label: string; info: string }> };

  const questions: Question[] = [
    {
      key: 'agents',
      title: 'What do your agents do today?',
      multi: true,
      choices: [
        { label: 'Write and ship code', info: 'TestMachine recognizes Claude Code and Codex when they sign in. You point them at your TestMachine endpoint in place of each MCP server, with no code changes.' },
        { label: 'Talk to customers', info: 'TestMachine can hold an outbound message that breaks a clause and tell the agent how to fix it before it goes out.' },
        { label: 'Run internal operations', info: 'TestMachine records every tool call with the agent, the tool, its arguments and the decision, so you see what your agents do before you write a rule.' },
        { label: 'We are not running agents yet', info: 'Most teams start in dry-run mode. TestMachine records what it would block and lets every call through, so you see a policy’s effect before it enforces.' }
      ]
    },
    {
      key: 'concern',
      title: 'What would you want TestMachine to stop first?',
      multi: true,
      choices: [
        { label: 'Prompt injection', info: 'We are building protection that holds tool calls following instructions hidden in an email, a web page or another tool’s result.' },
        { label: 'Messages that are off-brand', info: 'We are building checks of every outbound message against your company’s voice and disclosure rules, whichever agent wrote it.' },
        { label: 'Risky actions, like sending email', info: 'A clause can block the send and return your team’s instructions, such as drafting the email for a teammate to review. The agent takes the allowed path without a person stepping in.' },
        { label: 'Not sure yet', info: 'That is where the record helps. Watch what your agents do for a week, then write the first rules from what you see.' }
      ]
    },
    {
      key: 'volume',
      title: 'Roughly how many tool calls do your agents make a month?',
      multi: false,
      choices: [
        { label: 'Fewer than 1,000', info: 'That fits the Free plan, which includes 1,000 actions a month.' },
        { label: '1,000 to 100,000', info: 'On Pay as you go that is $10 per agent a month, plus about $1 to $100 for actions at $1 per 1,000.' },
        { label: '100,000 to 1 million', info: 'On Pay as you go that is $10 per agent a month, plus about $100 to $1,000 for actions, with no cap on people or agents.' },
        { label: 'More than 1 million', info: 'Enterprise adds a negotiated volume rate and deployment in your private cloud or on premises.' },
        { label: 'I do not know', info: 'TestMachine counts them for you. Free covers the first 1,000 actions each month.' }
      ]
    }
  ];

  const OTHER = 'Other';
  for (const q of questions) q.choices.push({ label: OTHER, info: 'Tell us in a few words, and we will cover it when we follow up.' });

  const total = questions.length + 2;

  let step = $state(0);
  let answers = $state<Record<Question['key'], string[]>>({ agents: [], concern: [], volume: [] });
  let other = $state<Record<Question['key'], string>>({ agents: '', concern: '', volume: '' });
  let form = $state({ company: '', jobTitle: '', firstName: '', lastName: '', email: '' });
  let errors = $state<Partial<Record<keyof typeof form, string>>>({});
  let submitting = $state(false);
  let failed = $state(false);
  let done = $state(false);

  const question = $derived(questions[step]);
  const picked = $derived(question ? question.choices.find((c) => c.label === answers[question.key].at(-1)) : undefined);

  function track(name: string, params: Record<string, string | number>) {
    send(name, { form: 'early_access', ...params });
  }

  let started = false;
  let source_cta = '';
  function start() {
    if (started) return;
    started = true;
    source_cta = lastCta();
    track('form_start', { source_cta });
  }

  function choose(q: Question, label: string) {
    start();
    const on = !answers[q.key].includes(label);
    answers[q.key] = q.multi ? (on ? [...answers[q.key], label] : answers[q.key].filter((l) => l !== label)) : [label];
    track('form_step_answer', { step: step + 1, question: q.key, answer: label, selected: on || !q.multi ? 1 : 0 });
  }

  function summary(key: Question['key']) {
    const text = other[key].trim();
    return answers[key].map((l) => (l === OTHER && text ? `Other: ${text}` : l)).join('; ');
  }

  function require(keys: Array<keyof typeof form>) {
    const next: typeof errors = {};
    for (const k of keys) if (!form[k].trim()) next[k] = 'Required';
    if (keys.includes('email') && form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'Enter a valid email';
    errors = next;
    return Object.keys(next).length === 0;
  }

  function next() {
    start();
    if (step === questions.length && !require(['company', 'jobTitle'])) return;
    track('form_step_complete', { step: step + 1, answered: step < questions.length ? answers[questions[step].key].length : 1 });
    step += 1;
    track('form_step_view', { step: step + 1 });
  }

  function back() {
    errors = {};
    step -= 1;
  }

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    if (!require(['firstName', 'lastName', 'email'])) return;
    source_cta ||= lastCta();
    submitting = true;
    failed = false;
    track('form_submit', { step: total, source_cta });
    const t = (s: string) => s.trim();
    try {
      await submitHubspotForm([
        { name: 'firstname', value: t(form.firstName) },
        { name: 'lastname', value: t(form.lastName) },
        { name: 'email', value: t(form.email) },
        {
          name: 'message',
          value: [
            '[early_access] TestMachine early access',
            `Company: ${t(form.company)}`,
            `Job title: ${t(form.jobTitle)}`,
            `Agents: ${summary('agents') || 'Skipped'}`,
            `Stop first: ${summary('concern') || 'Skipped'}`,
            `Monthly tool calls: ${summary('volume') || 'Skipped'}`
          ].join('\n')
        }
      ]);
      done = true;
      track('generate_lead', { agents: summary('agents'), concern: summary('concern'), volume: summary('volume'), source_cta, submit_status: 'success' });
    } catch (error) {
      console.error('Early access form error:', error);
      track('form_submit_failed', { step: total, error_type: submitErrorType(error), source_cta });
      failed = true;
    } finally {
      submitting = false;
    }
  }
</script>

<section id="early-access" class="early">
  <div class="wrap grid">
    <div>
      <p class="kicker">Early access</p>
      <h2>Get early access to TestMachine.</h2>
      <p class="lead">Answer five quick steps about your agents, and we will show you how TestMachine fits as you go. We are onboarding a small number of teams, and early-access teams use TestMachine free until billing launches.</p>
      <p class="book">To talk it through first, <a href={BOOKING_URL} onclick={() => trackCtaClick('early_access_book', 'Book Now', BOOKING_URL)}>book a call with the team</a>.</p>
    </div>
    <div class="panel">
      {#if done}
        <div class="done" role="status">
          <h3>You are on the list.</h3>
          <p>We will email {form.email.trim()} when there is a place for your team.</p>
        </div>
      {:else}
        <div class="progress">
          <span>Step {step + 1} of {total}</span>
          <div class="bar" aria-hidden="true"><i style="width: {((step + 1) / total) * 100}%"></i></div>
        </div>
        {#key step}
          <div class="slide" in:fly={{ x: 24, duration: 220 }}>
            {#if question}
              <fieldset>
                <legend>{question.title}{#if question.multi}<small>Choose any that apply</small>{/if}</legend>
                <div class="choices" role={question.multi ? 'group' : 'radiogroup'} aria-label={question.title}>
                  {#each question.choices as choice}
                    <label class="choice" class:on={answers[question.key].includes(choice.label)}>
                      <input type={question.multi ? 'checkbox' : 'radio'} name={question.key} value={choice.label} checked={answers[question.key].includes(choice.label)} onchange={() => choose(question, choice.label)} />
                      {choice.label}
                    </label>
                  {/each}
                </div>
                {#if answers[question.key].includes(OTHER)}
                  <input class="other" type="text" maxlength="200" placeholder="Tell us in a few words" aria-label="Other, in your own words" bind:value={other[question.key]} />
                {/if}
              </fieldset>
              {#key picked}
                {#if picked}<p class="info" in:fly={{ y: 8, duration: 200 }}>{picked.info}</p>{/if}
              {/key}
              <div class="actions">
                {#if step > 0}<button type="button" class="link" onclick={back}>Back</button>{/if}
                <button type="button" class="btn" onclick={next}>{picked ? 'Continue' : 'Skip'}</button>
              </div>
            {:else if step === questions.length}
              <h3>Tell us about your team.</h3>
              <div class="row">
                <label>
                  <span>Company<b class="req" aria-hidden="true">*</b></span>
                  <input required type="text" name="company" autocomplete="organization" bind:value={form.company} aria-invalid={!!errors.company} oninput={() => (errors.company = undefined)} />
                  {#if errors.company}<small class="error">{errors.company}</small>{/if}
                </label>
                <label>
                  <span>Job title<b class="req" aria-hidden="true">*</b></span>
                  <input required type="text" name="jobTitle" autocomplete="organization-title" bind:value={form.jobTitle} aria-invalid={!!errors.jobTitle} oninput={() => (errors.jobTitle = undefined)} />
                  {#if errors.jobTitle}<small class="error">{errors.jobTitle}</small>{/if}
                </label>
              </div>
              <div class="actions">
                <button type="button" class="link" onclick={back}>Back</button>
                <button type="button" class="btn" onclick={next}>Continue</button>
              </div>
            {:else}
              <form onsubmit={submit} novalidate>
                <h3>Last, the details for your invite.</h3>
                <div class="row">
                  <label>
                    <span>First name<b class="req" aria-hidden="true">*</b></span>
                    <input required type="text" name="firstName" autocomplete="given-name" bind:value={form.firstName} aria-invalid={!!errors.firstName} oninput={() => (errors.firstName = undefined)} />
                    {#if errors.firstName}<small class="error">{errors.firstName}</small>{/if}
                  </label>
                  <label>
                    <span>Last name<b class="req" aria-hidden="true">*</b></span>
                    <input required type="text" name="lastName" autocomplete="family-name" bind:value={form.lastName} aria-invalid={!!errors.lastName} oninput={() => (errors.lastName = undefined)} />
                    {#if errors.lastName}<small class="error">{errors.lastName}</small>{/if}
                  </label>
                  <label class="wide">
                    <span>Work email<b class="req" aria-hidden="true">*</b></span>
                    <input required type="email" name="email" autocomplete="email" bind:value={form.email} aria-invalid={!!errors.email} oninput={() => (errors.email = undefined)} />
                    {#if errors.email}<small class="error">{errors.email}</small>{/if}
                  </label>
                </div>
                <div class="actions">
                  <button type="button" class="link" onclick={back}>Back</button>
                  <button class="btn" type="submit" disabled={submitting} onclick={() => trackCtaClick('early_access_form', 'Get early access', 'hubspot_form')}>{submitting ? 'Sending…' : 'Get early access'}</button>
                </div>
                {#if failed}<p class="error">The request did not go through. Please try again, or email contact@testmachine.ai.</p>{/if}
              </form>
            {/if}
          </div>
        {/key}
      {/if}
    </div>
  </div>
</section>

<style>
  .early { padding: clamp(64px, 8vw, 104px) 0; border-top: 1px solid var(--border-subtle); }
  .wrap { width: 100%; max-width: 1200px; margin: 0 auto; padding: 0 clamp(20px, 4vw, 40px); }
  .grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: clamp(32px, 6vw, 88px); align-items: start; }
  .kicker { font: 600 0.8rem var(--font-ui); letter-spacing: 0.08em; text-transform: uppercase; color: var(--accent-hover); margin-bottom: 16px; }
  h2 { font: 600 clamp(1.8rem, 3.4vw, 2.6rem)/1.12 var(--font-display); letter-spacing: -0.02em; }
  h3 { font: 600 1.15rem/1.35 var(--font-ui); margin-bottom: 16px; }
  .lead { font: 1.05rem/1.65 var(--font-body); color: var(--fg-muted); max-width: 52ch; margin-top: 18px; }
  .book { font: 1rem/1.6 var(--font-body); color: var(--fg-muted); margin-top: 16px; }
  .book a { color: var(--fg); border-bottom: 1px solid var(--accent); }
  .panel { border: 1px solid var(--border); border-radius: 2px; background: var(--bg-deeper); padding: clamp(22px, 3vw, 36px); overflow: hidden; }
  .progress { display: grid; gap: 8px; margin-bottom: 24px; }
  .progress span { font: 500 0.75rem var(--font-mono); letter-spacing: 0.08em; text-transform: uppercase; color: var(--fg-muted); }
  .bar { height: 2px; background: var(--border-subtle); }
  .bar i { display: block; height: 100%; background: var(--accent); transition: width 0.25s; }
  fieldset { border: 0; padding: 0; margin: 0; }
  legend { font: 600 1.15rem/1.35 var(--font-ui); margin-bottom: 16px; padding: 0; }
  .choices { display: grid; gap: 8px; }
  .choice { display: flex; align-items: center; gap: 10px; font: 500 0.95rem var(--font-body); padding: 12px 14px; border: 1px solid var(--border); border-radius: 2px; cursor: pointer; background: var(--bg); }
  .choice.on { border-color: var(--fg); background: var(--bg-raised); }
  legend small { display: block; font: 400 0.85rem var(--font-body); color: var(--fg-muted); margin-top: 4px; }
  input.other { margin-top: 8px; }
  .choice input { accent-color: var(--accent-hover); }
  .info { font: 0.95rem/1.6 var(--font-body); color: var(--fg); background: var(--bg-raised); border: 1px solid var(--border-subtle); border-radius: 2px; padding: 14px 16px; margin-top: 16px; }
  form { display: grid; }
  .row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  label { display: grid; gap: 8px; font: 600 0.85rem var(--font-ui); align-content: start; }
  label.wide { grid-column: 1 / -1; }
  .req { font-weight: 600; color: var(--accent-hover); margin-left: 3px; }
  input:not([type='radio']):not([type='checkbox']) { font: 1rem var(--font-body); padding: 12px 14px; border: 1px solid var(--border); border-radius: 2px; background: var(--bg); color: var(--fg); width: 100%; }
  input:focus { outline: none; border-color: var(--accent-hover); }
  input[aria-invalid='true'] { border-color: var(--accent-red); }
  .actions { display: flex; align-items: center; justify-content: flex-end; gap: 20px; margin-top: 24px; }
  .link { font: 500 0.95rem var(--font-body); background: none; border: 0; padding: 0; color: var(--fg-muted); cursor: pointer; margin-right: auto; }
  .link:hover { color: var(--fg); }
  .btn { font: 500 0.95rem var(--font-body); padding: 13px 22px; border: 1px solid var(--accent); border-radius: 2px; background: var(--accent); color: var(--fg); cursor: pointer; transition: background 0.2s; }
  .btn:hover { background: var(--gold-fill); border-color: var(--gold-fill); }
  .btn:disabled { opacity: 0.6; cursor: default; }
  .error { font: 400 0.85rem var(--font-body); color: var(--accent-red); margin: 0; }
  form > .error { margin-top: 12px; }
  .done h3 { margin-bottom: 8px; }
  .done p { font: 1rem/1.6 var(--font-body); color: var(--fg-muted); }
  @media (max-width: 860px) { .grid { grid-template-columns: 1fr; } }
  @media (max-width: 560px) { .row { grid-template-columns: 1fr; } }
</style>
